import { buildPayment } from '@ims/core'
import type { CustomerRow, Page, PartyStanding, PaymentMethod, RowOf } from '@ims/data'
import {
  Avatar,
  Button,
  Checkbox,
  EmptyState,
  Field,
  Input,
  Kbd,
  ListFrame,
  ListToolbar,
  Modal,
  PageHeader,
  Pagination,
  RowActions,
  RowNote,
  RowTitle,
  SearchInput,
  Segmented,
  Select,
  TD,
  TH,
  TR,
  Table,
  Textarea,
  useToast,
  SkeletonTable,
  cx,
} from '@ims/ui'
import { Plus, Truck, Users } from '@ims/ui/icons'
import { useRef, useState } from 'react'
import { Link } from 'react-router'
import { MoneyInput, PhoneAdd } from '../components.tsx'
import { errorMessage, useFieldGroups, useLive, useSettings, useUrlAction } from '../hooks.ts'
import { allocateOldestFirst } from '../lib/allocate.ts'
import { dateShort, money, todayIso } from '../lib/format.ts'
import { useListKeys } from '../lib/list-keys.ts'
import { moveClick, useOpen } from '../lib/motion.ts'
import { daysBetween, dueWords, type Standing } from '../lib/purchases.ts'
import { insertOp, newCustomer, newSupplier, updateOp } from '../lib/rows.ts'
import { useCan, useRepo, useWorkspace } from '../workspace.tsx'
import { PaymentDialog } from './invoices.tsx'
import { PAYABLE_TABLES, PaySupplierDialog } from './supplier-bills.tsx'
import { finePointer } from '../lib/pointer.ts'

type Kind = 'customers' | 'suppliers'
type Party = RowOf<'customers'> | RowOf<'suppliers'>

/** What the list shows of one customer or supplier: the row, and where they stand. */
type BoardRow = { party: Party } & PartyStanding
type Show = 'all' | 'owing' | 'inactive'
const PAGE = 25
/** The row the arrow keys are on (as on Invoices and Quotations), in the list's colour. */
const ROW_ACTIVE = {
  customers: 'sm:bg-people-wash sm:shadow-[inset_3px_0_0_var(--color-people)]',
  suppliers: 'sm:bg-buy-wash sm:shadow-[inset_3px_0_0_var(--color-buy)]',
} as const
const ROW_BUTTON = 'h-[34px] text-[13px]'

/**
 * Customers or suppliers, 25 a page (docs/plans/lists-orders-style.md): what each owes and
 * since when, their last bill, and Take payment / Pay on the row. The search, the switch
 * and the pages run in the local query (`repo.parties`); online-only mode reads the names
 * and pages them here.
 */
function PartyList({ kind }: { kind: Kind }) {
  const settings = useSettings()
  const repo = useRepo()
  const openItem = useOpen()
  const isCustomers = kind === 'customers'
  // Waiters add customers (credit sales); suppliers are for staff.
  const canEdit = useCan(isCustomers ? 'waiter' : 'staff')
  const canSeeOwed = useCan('staff')
  // Taking a customer's payment is as on Invoices; paying a supplier needs staff.
  const canPay = useCan(isCustomers ? 'waiter' : 'staff')
  const [search, setSearchValue] = useState('')
  const [show, setShowValue] = useState<Show>('all')
  const [offset, setOffset] = useState(0)
  const setSearch = (v: string) => {
    setSearchValue(v)
    setOffset(0)
  }
  const setShow = (v: Show) => {
    setShowValue(v)
    setOffset(0)
  }
  const [editingState, setEditing] = useState<Party | 'new' | null>(null)
  const [askedNew, clearAskedNew] = useUrlAction('new')
  const editing = editingState ?? (askedNew && canEdit ? 'new' : null)
  const [paying, setPaying] = useState<BoardRow | null>(null)
  const today = todayIso()
  const tables = isCustomers
    ? (['customers', 'sales_invoices', 'credit_notes', 'payments', 'payment_allocations'] as const)
    : (['suppliers', ...PAYABLE_TABLES] as const)

  const page = useLive(
    async (r): Promise<Page<BoardRow>> => {
      const filter = {
        search,
        show: show === 'all' ? undefined : show,
        today,
        limit: PAGE,
        offset,
      }
      if (r.parties) return isCustomers ? r.parties.customers(filter) : r.parties.suppliers(filter)
      // Online-only: the names alone, searched and paged here.
      const term = search.trim().toLowerCase()
      const all = (await r.list(kind, { orderBy: { column: 'name' }, limit: 5000 })).rows.filter(
        (p) =>
          (show !== 'inactive' || !p.is_active) &&
          show !== 'owing' &&
          (!term ||
            [p.name, p.phone, p.email, p.pan_vat_number].some((v) =>
              v?.toLowerCase().includes(term),
            )),
      )
      return {
        total: all.length,
        rows: all.slice(offset, offset + PAGE).map((party) => ({
          party,
          owedPaisa: 0,
          openBills: 0,
          lateBills: 0,
          oldestOpenDate: null,
          firstDueDate: null,
          lastDate: null,
          lastTotalPaisa: null,
        })),
      }
    },
    [kind, search, show, offset, today],
    [...tables],
  )
  const summary = useLive(
    async (r) => (r.parties ? r.parties.summary(kind, today) : null),
    [kind, today],
    [...tables],
  ).data
  const owedOn = !!repo.parties && canSeeOwed

  const rows = page.data?.rows ?? []
  const [active, setActive] = useState<string | null>(null)
  const activeRow = rows.find((r) => r.party.id === active)
  const payable = (r: BoardRow) => owedOn && canPay && r.owedPaisa > 0
  const listRef = useRef<HTMLDivElement>(null)
  useListKeys({
    ids: rows.map((r) => r.party.id),
    active,
    setActive,
    page: { offset, limit: PAGE, total: page.data?.total ?? 0, setOffset },
    list: listRef,
    open: () => activeRow && openItem(`/${kind}/${activeRow.party.id}`),
    keys: {
      [isCustomers ? 't' : 'p']: () => activeRow && payable(activeRow) && setPaying(activeRow),
    },
  })

  const Icon = isCustomers ? Users : Truck
  const area = isCustomers ? 'people' : 'buy'
  const word = isCustomers ? 'customer' : 'supplier'
  const amount = (p: number) => money(p, settings)
  const short = (d: string) => dateShort(d, settings)

  const standing = (r: BoardRow): Standing => {
    if (r.owedPaisa <= 0) return { main: 'Nothing owed' }
    const bills = `${r.openBills} bill${r.openBills === 1 ? '' : 's'}`
    const when =
      r.lateBills > 0
        ? `${r.lateBills} late`
        : r.firstDueDate
          ? dueWords(r.firstDueDate.slice(0, 10), today, short, 'by').text
          : r.oldestOpenDate
            ? oldestWords(r.oldestOpenDate.slice(0, 10), today)
            : ''
    return {
      main: `${amount(r.owedPaisa)} ${isCustomers ? 'due' : 'to pay'}`,
      sub: when ? `${bills} · ${when}` : bills,
      tone: r.lateBills > 0 ? 'danger' : 'warning',
    }
  }

  return (
    <div>
      <PageHeader
        title={isCustomers ? 'Customers' : 'Suppliers'}
        subtitle={
          canSeeOwed && summary && summary.owedPaisa > 0 ? (
            <>
              <b className="font-semibold text-ink">
                {amount(summary.owedPaisa)} {isCustomers ? 'owed to you' : 'to pay'}
              </b>{' '}
              {isCustomers ? 'by' : 'to'} {summary.owing} {word}
              {summary.owing === 1 ? '' : 's'}
              {summary.late > 0 && (
                <span className="font-semibold text-danger"> · {summary.late} late</span>
              )}
            </>
          ) : isCustomers ? (
            'People and businesses you sell to.'
          ) : (
            'Who you buy stock from.'
          )
        }
        actions={
          canEdit && (
            // Below 1024px the bottom bar's + is the one solid button: Add moves to the search row.
            <Button
              icon={<Plus className="size-4" />}
              onClick={() => setEditing('new')}
              className="max-lg:hidden"
            >
              Add {word}
            </Button>
          )
        }
      />
      <ListToolbar>
        <Segmented
          label={isCustomers ? 'Which customers' : 'Which suppliers'}
          value={show}
          onChange={setShow}
          options={[
            { value: 'all', label: 'All' },
            ...(owedOn
              ? [{ value: 'owing' as const, label: isCustomers ? 'Owe you' : 'You owe' }]
              : []),
            { value: 'inactive', label: 'Inactive' },
          ]}
          className="max-sm:w-full"
        />
        <div className="flex gap-2 max-sm:w-full sm:ml-auto">
          <SearchInput
            listKeys
            className="min-w-0 max-sm:flex-1 sm:w-80"
            value={search}
            onChange={setSearch}
            placeholder="Name, phone, email or PAN…"
          />
          {canEdit && <PhoneAdd label={`Add ${word}`} onClick={() => setEditing('new')} />}
        </div>
      </ListToolbar>
      <ListFrame ref={listRef}>
        {!page.data ? (
          <SkeletonTable />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Icon className="size-6" />}
            title={
              search
                ? 'No matches'
                : show === 'owing'
                  ? 'Nothing owed'
                  : show === 'inactive'
                    ? `No inactive ${kind}`
                    : `No ${kind} yet`
            }
            description={
              search || show !== 'all'
                ? undefined
                : isCustomers
                  ? 'Add the people and businesses you sell to on credit, or who want their name on the bill.'
                  : 'Add who you buy from, then choose them when you receive stock.'
            }
          />
        ) : (
          <>
            <Table stack="cards">
              <thead>
                <tr>
                  <TH>{isCustomers ? 'Customer' : 'Supplier'}</TH>
                  {owedOn && <TH>{isCustomers ? 'Last bill' : 'Last purchase'}</TH>}
                  {owedOn && <TH>Where it stands</TH>}
                  {owedOn && <TH align="right">{isCustomers ? 'Owes you' : 'You owe'}</TH>}
                  {!owedOn && <TH>PAN/VAT</TH>}
                  <TH />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const p = r.party
                  const to = `/${kind}/${p.id}`
                  return (
                    <TR
                      key={p.id}
                      onClick={() => openItem(to)}
                      aria-current={active === p.id ? 'true' : undefined}
                      className={cx(active === p.id && ROW_ACTIVE[kind])}
                    >
                      <TD className="stack-title">
                        <RowTitle
                          tile={<Avatar name={p.name} area={area} />}
                          title={
                            <>
                              <Link
                                to={to}
                                className="hover:underline focus-visible:underline"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  moveClick(to, 'open')(e)
                                }}
                              >
                                {p.name}
                              </Link>
                              {!p.is_active && (
                                <span className="ml-2 text-xs font-normal text-muted">
                                  inactive
                                </span>
                              )}
                            </>
                          }
                          sub={p.phone ?? 'No phone'}
                        />
                      </TD>
                      {owedOn && (
                        <TD className="whitespace-nowrap text-[13px] text-muted max-sm:hidden">
                          {r.lastDate
                            ? `${short(r.lastDate)} · ${amount(r.lastTotalPaisa ?? 0)}`
                            : isCustomers
                              ? 'No bills yet'
                              : 'No purchases yet'}
                        </TD>
                      )}
                      {owedOn && (
                        <TD className={cx('stack-r3', r.owedPaisa <= 0 && 'max-sm:hidden')}>
                          <RowNote {...standing(r)} />
                        </TD>
                      )}
                      {owedOn && (
                        <TD
                          align="right"
                          className="stack-end stack-r1 whitespace-nowrap font-bold"
                        >
                          {r.owedPaisa > 0 ? (
                            amount(r.owedPaisa)
                          ) : (
                            <span className="font-normal text-subtle max-sm:hidden">—</span>
                          )}
                        </TD>
                      )}
                      {!owedOn && (
                        <TD className="stack-r3 text-muted max-sm:text-xs">
                          {p.pan_vat_number ?? '—'}
                        </TD>
                      )}
                      <TD className="stack-actions sm:w-px sm:whitespace-nowrap">
                        <RowActions>
                          {payable(r) && (
                            <Button
                              size="sm"
                              variant="soft"
                              className={ROW_BUTTON}
                              aria-label={`${isCustomers ? 'Take payment from' : 'Pay'} ${p.name}`}
                              onClick={() => setPaying(r)}
                            >
                              {isCustomers ? 'Take payment' : 'Pay'}
                            </Button>
                          )}
                        </RowActions>
                      </TD>
                    </TR>
                  )
                })}
              </tbody>
            </Table>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5 text-xs text-muted max-sm:px-1">
              <span>A to Z. A row opens the {word}.</span>
              <span className="ml-auto flex items-center gap-1.5 max-lg:hidden">
                <Kbd>↑</Kbd> <Kbd>↓</Kbd> move <Kbd>Enter</Kbd> open{' '}
                {owedOn && canPay && (
                  <>
                    <Kbd>{isCustomers ? 'T' : 'P'}</Kbd> {isCustomers ? 'take payment' : 'pay'}
                  </>
                )}
              </span>
            </div>
            <Pagination offset={offset} limit={PAGE} total={page.data.total} onChange={setOffset} />
          </>
        )}
      </ListFrame>
      {editing && (
        <PartyForm
          kind={kind}
          party={editing === 'new' ? null : editing}
          onClose={() => {
            setEditing(null)
            if (askedNew) clearAskedNew()
          }}
        />
      )}
      {paying && isCustomers && (
        <CustomerPaymentDialog
          customer={paying.party as CustomerRow}
          owedPaisa={paying.owedPaisa}
          onClose={() => setPaying(null)}
        />
      )}
      {paying && !isCustomers && (
        <PaySupplierDialog
          supplierId={paying.party.id}
          supplierName={paying.party.name}
          onClose={() => setPaying(null)}
        />
      )}
    </div>
  )
}

/** "12 days old" for the oldest open bill with no due date. */
function oldestWords(date: string, today: string): string {
  const days = daysBetween(date, today)
  if (days <= 0) return 'from today'
  return `oldest ${days} day${days === 1 ? '' : 's'} old`
}

/**
 * Take a payment from a customer: it starts at what they owe and is set against their open
 * bills oldest first, in one commit (core `buildPayment`, one allocation per bill). With
 * `ahead` (the customer's page) it may be more than they owe: the rest is paid ahead.
 */
export function CustomerPaymentDialog({
  customer,
  owedPaisa,
  onClose,
  ahead = false,
  onShare,
}: {
  customer: CustomerRow
  owedPaisa: number
  onClose: () => void
  ahead?: boolean
  /** Offered on the "Payment recorded" toast: share the statement now. */
  onShare?: () => void
}) {
  const repo = useRepo()
  const toast = useToast()
  const { current } = useWorkspace()
  const [busy, setBusy] = useState(false)
  const pay = async (amount: number, method: PaymentMethod, reference: string) => {
    if (!current || !repo.reports) return
    setBusy(true)
    try {
      // Read the bills as they are now, at the moment of the write.
      const open = (await repo.reports.openInvoices()).filter((o) => o.customerId === customer.id)
      await repo.commit(
        buildPayment(
          { tenantId: current.tenant_id, now: new Date().toISOString() },
          {
            customerId: customer.id,
            amountPaisa: amount,
            method,
            paymentDate: todayIso(),
            reference: reference || null,
            allocations: allocateOldestFirst(open, amount),
          },
        ).ops,
      )
      toast.success(
        'Payment recorded',
        onShare ? { label: 'Share statement', onClick: onShare } : undefined,
      )
      onClose()
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }
  return (
    <PaymentDialog
      title={
        owedPaisa > 0
          ? `Take payment from ${customer.name}`
          : `Take an advance from ${customer.name}`
      }
      ahead={ahead}
      balancePaisa={owedPaisa}
      busy={busy}
      onClose={onClose}
      onSubmit={(amount, method, reference) => void pay(amount, method, reference)}
      detail={owedPaisa > 0 ? 'Set against their oldest bills first.' : undefined}
    />
  )
}

export const CustomersScreen = () => <PartyList kind="customers" />
export const SuppliersScreen = () => <PartyList kind="suppliers" />

export function PartyForm({
  kind,
  party,
  onClose,
}: {
  kind: Kind
  party: Party | null
  onClose: () => void
}) {
  const repo = useRepo()
  const { current } = useWorkspace()
  const toast = useToast()
  const [name, setName] = useState(party?.name ?? '')
  const [contact, setContact] = useState(party?.contact_name ?? '')
  const [phone, setPhone] = useState(party?.phone ?? '')
  const [email, setEmail] = useState(party?.email ?? '')
  const [pan, setPan] = useState(party?.pan_vat_number ?? '')
  const [address, setAddress] = useState(party?.address ?? '')
  const [notes, setNotes] = useState(party?.notes ?? '')
  const [creditLimit, setCreditLimit] = useState<number | null>(
    party && 'credit_limit_paisa' in party ? party.credit_limit_paisa : null,
  )
  const [tier, setTier] = useState<'retail' | 'wholesale'>(
    party && 'price_tier' in party ? party.price_tier : 'retail',
  )
  const { groups } = useFieldGroups()
  const [active, setActive] = useState(party?.is_active ?? true)
  const [busy, setBusy] = useState(false)

  const save = async () => {
    if (!current || !name.trim()) return toast.error('Name is required')
    setBusy(true)
    const fields = {
      name: name.trim(),
      contact_name: contact.trim() || null,
      phone: phone.trim() || null,
      email: email.trim() || null,
      pan_vat_number: pan.trim() || null,
      address: address.trim() || null,
      notes: notes.trim() || null,
      is_active: active,
    }
    try {
      const ctx = { tenantId: current.tenant_id }
      const op = party
        ? updateOp(
            kind,
            party as never,
            kind === 'customers'
              ? { ...fields, credit_limit_paisa: creditLimit, price_tier: tier }
              : fields,
          )
        : kind === 'customers'
          ? insertOp(
              'customers',
              newCustomer(ctx, { ...fields, credit_limit_paisa: creditLimit, price_tier: tier }),
            )
          : insertOp('suppliers', newSupplier(ctx, fields))
      await repo.commit([op])
      toast.success('Saved')
      onClose()
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`${party ? 'Edit' : 'Add'} ${kind === 'customers' ? 'customer' : 'supplier'}`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} onClick={() => void save()}>
            Save
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name" className="sm:col-span-2">
          {(id) => (
            <Input
              id={id}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus={finePointer}
            />
          )}
        </Field>
        <Field label="Contact person">
          {(id) => <Input id={id} value={contact} onChange={(e) => setContact(e.target.value)} />}
        </Field>
        <Field label="Phone">
          {(id) => (
            <Input id={id} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
          )}
        </Field>
        <Field label="Email">
          {(id) => (
            <Input id={id} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          )}
        </Field>
        <Field label="PAN / VAT number">
          {(id) => <Input id={id} value={pan} onChange={(e) => setPan(e.target.value)} />}
        </Field>
        <Field label="Address" className="sm:col-span-2">
          {(id) => <Input id={id} value={address} onChange={(e) => setAddress(e.target.value)} />}
        </Field>
        {kind === 'customers' && (
          <Field label="Credit limit (optional)">
            {(id) => (
              <MoneyInput
                id={id}
                paisa={creditLimit}
                onValue={setCreditLimit}
                placeholder="No limit"
              />
            )}
          </Field>
        )}
        {kind === 'customers' && (groups.has('wholesale') || tier === 'wholesale') && (
          <Field label="Prices" hint="Wholesale customers are billed at wholesale prices.">
            {(id) => (
              <Select
                id={id}
                value={tier}
                onChange={(e) => setTier(e.target.value === 'wholesale' ? 'wholesale' : 'retail')}
              >
                <option value="retail">Retail</option>
                <option value="wholesale">Wholesale</option>
              </Select>
            )}
          </Field>
        )}
        <div className="flex items-end pb-2">
          <Checkbox label="Active" checked={active} onChange={(e) => setActive(e.target.checked)} />
        </div>
        <Field label="Notes" className="sm:col-span-2">
          {(id) => <Textarea id={id} value={notes} onChange={(e) => setNotes(e.target.value)} />}
        </Field>
      </div>
    </Modal>
  )
}
